import SEO from "@/components/common/SEO";
import PageHeader from "@/components/shared/PageHeader";
import { Reveal, Icon } from "@/components/common/motion";
import { ContactCTA } from "@/components/shared/blocks";
import { caseStudies } from "@/data/site";
import { TrendingUp } from "lucide-react";

export default function CaseStudies() {
  return (
    <>
      <SEO title="Case Studies" description="Real client outcomes from NeuroTriq TechLabs — challenges, solutions, technology, results, business impact and ROI." path="/case-studies" />
      <PageHeader
        overline="Case Studies"
        title="Results that speak for themselves"
        subtitle="How we've helped clients cut costs, ship faster and delight their customers — with the numbers to prove it."
      />

      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-6xl space-y-20 px-5 sm:px-8">
          {caseStudies.map((cs, i) => (
            <Reveal key={cs.id}>
              <article className="overflow-hidden rounded-3xl border border-border bg-card" data-testid={`casestudy-${cs.id}`}>
                <div className="grid lg:grid-cols-2">
                  <div className={`relative h-64 overflow-hidden lg:h-auto ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                    <img src={cs.image} alt={cs.title} loading="lazy" className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-secondary/50 to-transparent lg:bg-gradient-to-r" />
                    <span className="absolute left-5 top-5 rounded-full bg-card/90 px-3 py-1 text-xs font-medium backdrop-blur">{cs.industry}</span>
                  </div>
                  <div className="p-8 sm:p-10">
                    <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">{cs.client}</p>
                    <h2 className="mt-3 font-display text-2xl font-medium tracking-tight md:text-3xl">{cs.title}</h2>

                    <div className="mt-6 space-y-5">
                      <div>
                        <h3 className="text-sm font-semibold text-foreground">The challenge</h3>
                        <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{cs.challenge}</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground">Our solution</h3>
                        <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{cs.solution}</p>
                      </div>
                    </div>

                    <div className="mt-6 flex flex-wrap gap-1.5">
                      {cs.technology.map((t) => (
                        <span key={t} className="rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground">{t}</span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Results */}
                <div className="border-t border-border bg-muted/50 p-8 sm:p-10">
                  <div className="grid gap-6 sm:grid-cols-3">
                    {cs.results.map((r) => (
                      <div key={r.label} className="rounded-2xl border border-border bg-card p-5 text-center">
                        <p className="font-display text-4xl font-semibold text-primary">{r.metric}</p>
                        <p className="mt-1 text-sm text-muted-foreground">{r.label}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 grid gap-4 sm:grid-cols-[1fr_auto] sm:items-center">
                    <p className="text-sm text-muted-foreground leading-relaxed"><span className="font-semibold text-foreground">Business impact: </span>{cs.impact}</p>
                    <div className="flex items-center gap-3 rounded-2xl bg-success/10 px-5 py-3 text-success">
                      <TrendingUp className="h-5 w-5" />
                      <span className="text-sm font-medium">{cs.roi}</span>
                    </div>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <ContactCTA />
    </>
  );
}
